import React, { Component } from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import difference from 'lodash/difference';
import noop from 'lodash/noop';
import {
  Input,
  Row,
  Col,
  Modal,
  Tag,
  Table,
  Button,
  Icon,
  Tabs,
  TabPane,
} from 'components/ui';
import './style.css';

class ListRecipients extends Component {
  state = {
    showModal: false,
    activeKey: -1,
  };

  handleClick = activeKey => () => {
    this.setState({ activeKey, showModal: true });
  };

  handleKeyUp = event => {
    if (event.which === 13) this.handleHideModal();
  };

  handleEditRecipient = key => () => {
    this.setState({ activeKey: key, showModal: true });
  };

  handleInputChange = event => {
    const { name, value } = event.target;
    const recipients = new Map(this.props.recipients);
    const recipient = recipients.get(this.state.activeKey);
    recipient[name] = value;
    recipients.set(this.state.activeKey, recipient);
    this.props.onUpdateRecipients(recipients);
  };

  handleVariableChange = event => {
    const { activeKey } = this.state;
    const { name, value } = event.target;
    const recipients = new Map(this.props.recipients);
    const recipient = recipients.get(activeKey);
    recipients.set(activeKey, { ...recipient, [name]: value });
    this.props.onUpdateRecipients(recipients);
  };

  handleRemoveRecipient = key => () => {
    const recipients = new Map(this.props.recipients);
    recipients.delete(key);
    this.props.onUpdateRecipients(recipients);
  };

  handleHideEditModal = () => {
    this.setState({ showModal: false });
  };

  handleGetPrevRecipient = () => {
    this.setState(prevState => ({
      activeKey: prevState.activeKey - 1,
    }));
  };

  handleGetNextRecipient = () => {
    this.setState(prevState => ({
      activeKey: prevState.activeKey + 1,
    }));
  };

  getHasPrev = () => {
    const prevKey = this.state.activeKey - 1;
    return this.props.recipients.has(prevKey);
  };

  getHasNext = () => {
    const nextKey = this.state.activeKey + 1;
    return this.props.recipients.has(nextKey);
  };

  handleHideModal = () => {
    this.setState({ showModal: false });
  };

  getModalFooter = () => (
    <Button
      className="list-recipients_ok-btn"
      key="ok"
      onClick={this.handleHideModal}
    >
      Ok
    </Button>
  );

  getTableColumns = () => [
    {
      title: 'Variable',
      dataIndex: 'key',
      key: 'key',
      className: 'list-recipients_col',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      className: 'list-recipients_col',
      render: (_, data) => (
        <Input
          className="list-recipients__name-input"
          disabled={this.props.disableEdit}
          name={data.key}
          placeholder={data.key}
          value={data.value}
          onChange={this.handleVariableChange}
          onKeyUp={this.handleKeyUp}
        />
      ),
    },
  ];

  getTableData = () => {
    const { templateVariables } = this.props;
    const { template } = this.props;
    const { amp_replace_magic_link: replaceTokenAutomatically } = template;
    const recipient = this.props.recipients.get(this.state.activeKey);
    let keys = uniq([...templateVariables, ...Object.keys(recipient)]);
    keys = difference(keys, ['name', 'email']);
    return map(keys, key => ({
      key,
      value:
        replaceTokenAutomatically && key === 'token'
          ? "[Will be automatically replaced from Developer's Matching Database]"
          : recipient[key] || '',
    }));
  };

  getTags = () => {
    const tags = [];
    const { disableDelete, disableEdit } = this.props;
    let recipients = this.props.recipients;

    //initializing start and end index of the loop based on the keys of the first
    //and last entries of the recipients Map();
    let [loopStartKey] = recipients.keys();
    loopStartKey = loopStartKey || 0;
    let loopEndKey = recipients.size;
    if (loopEndKey > 0) {
      loopEndKey = [...recipients][recipients.size - 1][0];
    }

    // display a maximum of 1000 recipients
    let tagsCount = 0;
    for (
      let key = loopStartKey;
      key <= loopEndKey && tagsCount < 1000;
      key += 1
    ) {
      const value = this.props.recipients.get(key);
      if (value) {
        const { name, email } = value;
        const index = Number(key);
        tags.push(
          <Tag key={index}>
            <span
              id={`row-${key}`}
              role="button"
              tabIndex="-1"
              title="Click to view"
              onClick={this.handleClick(key)}
              onKeyUp={this.handleKeyUp}
            >
              {`${name}: ${email}`}
            </span>
            &nbsp;
            {!disableEdit && (
              <Icon
                className="list-recipients__edit-icon"
                id={`edit-${key}`}
                title="edit"
                type="edit"
                onClick={this.handleEditRecipient(key)}
              />
            )}
            {!disableDelete && (
              <Icon
                className="list-recipients__delete-icon"
                id={`delete-${key}`}
                title="Delete"
                type="delete"
                onClick={this.handleRemoveRecipient(key)}
              />
            )}
          </Tag>,
        );

        tagsCount = tagsCount + 1;
      }
    }
    return tags;
  };

  render() {
    const { showModal, activeKey } = this.state;
    const { disableEdit, recipients } = this.props;
    const activeRecipient = this.props.recipients.get(activeKey);

    if (!recipients.size) return null;

    return (
      <div className="list-recipients">
        <div className="list-recipients__preview">{this.getTags()}</div>

        {showModal && (
          <Modal
            centered
            footer={this.getModalFooter()}
            visible={showModal}
            width="80vw"
            onCancel={this.handleHideModal}
          >
            <div className="list-recipients__modal-body">
              <div className="list-recipients__nav">
                {this.getHasPrev() && (
                  <Icon
                    className="list-recipients__nav-left"
                    type="left"
                    onClick={this.handleGetPrevRecipient}
                  />
                )}
                {this.getHasNext() && (
                  <Icon
                    className="list-recipients__nav-right"
                    type="right"
                    onClick={this.handleGetNextRecipient}
                  />
                )}
              </div>
              <Tabs defaultActiveKey="variables">
                <TabPane key="variables" tab="View variables">
                  <Row gutter={8}>
                    <Col span={12}>
                      <Input
                        className="list-recipients__name-input"
                        disabled={disableEdit}
                        label="Recipient's name"
                        name="name"
                        placeholder="Name"
                        value={activeRecipient.name}
                        onChange={this.handleInputChange}
                        onKeyUp={this.handleKeyUp}
                      />
                    </Col>
                    <Col span={12}>
                      <Input
                        className="list-recipients__email-input"
                        disabled={disableEdit}
                        label="Recipient's email"
                        name="email"
                        placeholder="Email"
                        value={activeRecipient.email}
                        onChange={this.handleInputChange}
                        onKeyUp={this.handleKeyUp}
                      />
                    </Col>
                  </Row>
                  <div className="list-recipients__variables">
                    <h5>Template variables</h5>
                    <Table
                      className="list-recipients_template-variables"
                      columns={this.getTableColumns()}
                      dataSource={this.getTableData()}
                      pagination={false}
                    />
                  </div>
                </TabPane>
              </Tabs>
            </div>
          </Modal>
        )}
      </div>
    );
  }
}

ListRecipients.defaultProps = {
  disableDelete: false,
  disableEdit: false,
  template: {},
  templateVariables: [],
  onUpdateRecipients: noop,
};

ListRecipients.propTypes = {
  disableDelete: PropTypes.bool,
  disableEdit: PropTypes.bool,
  recipients: PropTypes.instanceOf(Map).isRequired,
  template: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    subject: PropTypes.string,
    template: PropTypes.string,
  }),
  templateVariables: PropTypes.array,
  onUpdateRecipients: PropTypes.func,
};

export default ListRecipients;
